/* eslint-disable no-restricted-syntax */
import type { ContainerListResponseApi } from 'types/api/container/container'
import type { ContainerListBody } from 'types/api/container/containerRequests'
import type { PlaceholderKind } from 'types/entities/shared'

// Vite substitutes `process.env.REACT_APP_*` at build time; declared here so `@types/node` stays out.
declare const process: { env: Record<string, string | undefined> }

const ENV_FLAG = process.env.REACT_APP_INVENTORY_PLACEMENT_MOCKS === 'true'

const MOCK_STORAGE_KEY = 'hh.placementMocks'

const storedToggle = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(MOCK_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

// Persists the mock toggle across reloads via localStorage.
export const setPlacementMocks = (isOn: boolean) => {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(MOCK_STORAGE_KEY, String(isOn))
  } catch {
    // A private window refuses storage; the window toggle below still works for the session.
  }
}

const BADGE_ID = 'hh-placement-mock-badge'

// Paints an on-screen badge so invented numbers are visibly mocked.
const paintBadge = (isOn: boolean) => {
  if (typeof document === 'undefined' || !document.body) return
  const existing = document.getElementById(BADGE_ID)
  if (!isOn) {
    existing?.remove()

    return
  }

  if (existing) return
  const badge = document.createElement('button')
  badge.id = BADGE_ID
  badge.type = 'button'
  badge.textContent = 'Placement mocks ON — container flags are invented. Click to turn off.'
  badge.title = 'Placeholder and customer-owned flags are stamped locally. Counts and chips are not real data.'
  badge.setAttribute(
    'style',
    [
      'position:fixed',
      'left:12px',
      'bottom:12px',
      'z-index:9999',
      'padding:6px 12px',
      'border-radius:4px',
      'border:1px solid #b26a00',
      'background:#fff4e5',
      'color:#663c00',
      'font:500 11px/1.4 Inter, system-ui, sans-serif',
      'cursor:pointer',
      'box-shadow:0 2px 4px rgba(0,0,0,.2)',
    ].join(';')
  )
  badge.addEventListener('click', () => {
    setPlacementMocks(false)
    window.location.reload()
  })
  document.body.appendChild(badge)
}

// Read per fetch, so flipping either toggle takes effect on the next request without a reload.
export const isPlacementMockOn = (): boolean => {
  const isOn =
    ENV_FLAG ||
    storedToggle() ||
    // eslint-disable-next-line no-underscore-dangle -- runtime devtools toggle
    (typeof window !== 'undefined' && (window as { __USE_PLACEMENT_MOCKS__?: boolean }).__USE_PLACEMENT_MOCKS__ === true)

  paintBadge(isOn)

  return isOn
}

export interface PlacementFlagsStamp {
  placeholder: boolean
  placeholderType: PlaceholderKind | null
  customerOwned: boolean
}

export interface PlacementMockRow {
  id: string
  name: string | null
}

const ASSET: PlacementFlagsStamp = { placeholder: false, placeholderType: null, customerOwned: false }
const PENDING: PlacementFlagsStamp = { placeholder: true, placeholderType: 'PENDING', customerOwned: false }
const NONE_EXPECTED: PlacementFlagsStamp = { placeholder: true, placeholderType: 'NONE_EXPECTED', customerOwned: false }
const CUSTOMER_OWNED: PlacementFlagsStamp = { placeholder: false, placeholderType: null, customerOwned: true }
const CUSTOMER_OWNED_PENDING: PlacementFlagsStamp = { placeholder: true, placeholderType: 'PENDING', customerOwned: true }

// Indexed by the id's last hex digit, so a container reads the same on the inventory list, its service and its work orders.
export const PLACEMENT_BUCKETS: readonly PlacementFlagsStamp[] = [
  PENDING,
  PENDING,
  PENDING,
  NONE_EXPECTED,
  CUSTOMER_OWNED,
  CUSTOMER_OWNED,
  CUSTOMER_OWNED_PENDING,
  ASSET,
  ASSET,
  ASSET,
  ASSET,
  ASSET,
  ASSET,
  ASSET,
  ASSET,
  ASSET,
]

const PENDING_MARKER = /\[pending\]/i
const NONE_MARKER = /\[none\]/i
const CUSTOMER_MARKER = /\[customer\]/i

const OVERRIDE_STORAGE_KEY = 'hh.placementMockOverrides'

// The real fields are not on any write payload yet, so a saved answer is remembered here instead.
let overrides: Record<string, PlacementFlagsStamp> | null = null

const loadOverrides = (): Record<string, PlacementFlagsStamp> => {
  if (overrides) return overrides
  overrides = {}
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(OVERRIDE_STORAGE_KEY) : null
    if (raw) overrides = JSON.parse(raw) as Record<string, PlacementFlagsStamp>
  } catch {
    overrides = {}
  }

  return overrides
}

const persistOverrides = (next: Record<string, PlacementFlagsStamp>) => {
  overrides = next
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // A private window refuses storage; the in-memory copy still serves this session.
  }
}

// Records a surface's "saved" answer so the row reads that way on later fetches.
export const setPlacementFlagsOverride = (id: string, stamp: PlacementFlagsStamp) => {
  if (!id) return
  persistOverrides({ ...loadOverrides(), [id]: stamp })
}

// Drops one remembered answer, letting the row fall back to its marker or id bucket.
export const clearPlacementFlagsOverride = (id: string) => {
  const next = { ...loadOverrides() }
  delete next[id]
  persistOverrides(next)
}

// Drops every remembered answer — the reset for a testing session.
export const clearAllPlacementFlagsOverrides = () => persistOverrides({})

// Surfaces check this before offering a write the BE would reject without the mock.
export const canWritePlacementFlags = (): boolean => isPlacementMockOn()

const kindFromMarkers = (pending: boolean, none: boolean): PlaceholderKind | null => {
  if (none) return 'NONE_EXPECTED'
  return pending ? 'PENDING' : null
}

// A marker in the name beats the bucket, so a hand-added container can be steered into any state.
const stampFromName = (name: string): PlacementFlagsStamp | null => {
  const pending = PENDING_MARKER.test(name)
  const none = NONE_MARKER.test(name)
  const customer = CUSTOMER_MARKER.test(name)
  if (!pending && !none && !customer) return null

  return { placeholder: pending || none, placeholderType: kindFromMarkers(pending, none), customerOwned: customer }
}

const stampFromId = (id: string): PlacementFlagsStamp => {
  const digit = parseInt(id.slice(-1), 16)
  return Number.isNaN(digit) ? ASSET : PLACEMENT_BUCKETS[digit]
}

// A saved answer wins over both guesses; without one the name marker still beats the id bucket.
export const placementFlagsFor = (row: PlacementMockRow): PlacementFlagsStamp =>
  loadOverrides()[row.id] ?? stampFromName(row.name ?? '') ?? stampFromId(row.id ?? '')

export const stampPlacementFlags = <T extends PlacementMockRow>(row: T): T =>
  isPlacementMockOn() ? { ...row, ...placementFlagsFor(row) } : row

export const stampPlacementFlagsOnList = <T extends { containers: PlacementMockRow[] | null }>(response: T): T => {
  if (!isPlacementMockOn() || !Array.isArray(response?.containers)) return response
  return { ...response, containers: response.containers.map(row => ({ ...row, ...placementFlagsFor(row) })) }
}

interface DraftContainersResponse {
  newContainers?: PlacementMockRow[] | null
  existingContainers?: PlacementMockRow[] | null
}

// A draft's new container is a slot with nothing behind it yet, which is what a pending placement is.
export const stampPlacementFlagsOnDraft = <T extends DraftContainersResponse>(response: T): T => {
  if (!isPlacementMockOn() || !response) return response
  const saved = loadOverrides()
  const next = { ...response }
  if (Array.isArray(response.newContainers)) {
    next.newContainers = response.newContainers.map(row => ({ ...row, ...(saved[row.id] ?? PENDING) }))
  }

  if (Array.isArray(response.existingContainers)) {
    next.existingContainers = response.existingContainers.map(row => ({ ...row, ...placementFlagsFor(row) }))
  }

  return next
}

// The create response is the first time a new row has an id, so ownership is remembered against it here.
export const rememberOwnershipOnCreate = (
  response: ContainerListResponseApi,
  meta: unknown,
  arg: ContainerListBody
): ContainerListResponseApi => {
  if (!isPlacementMockOn() || !Array.isArray(response?.containers)) return response
  response.containers.forEach(({ id }, index) => {
    const customerOwned = (arg?.containers?.[index] as { customerOwned?: boolean })?.customerOwned === true
    setPlacementFlagsOverride(String(id), { placeholder: false, placeholderType: null, customerOwned })
  })

  return response
}
